import React, { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { FaCheckCircle, FaChevronDown, FaArrowRight, FaUsers, FaChartLine, FaDumbbell, FaLaptop, FaTrophy, FaFileAlt, FaBuilding, FaUserPlus, FaCalendarAlt, FaClock } from 'react-icons/fa'
import TrustedPartners from './TrustedPartners'
import { CorporateWellnessCharts } from './CorporateWellnessCharts'

/* ───────────────────────────── onboarding timeline ───────────────────────────── */
const onboardingSteps = [
    {
        step: 1,
        icon: <FaBuilding className="text-white text-lg" />,
        iconBg: 'from-orange-500 to-amber-500',
        title: 'Company Onboarding',
        duration: '~1 week',
        description: 'After an initial meeting to discuss your wellness goals and select the right program, StringsSG sets up your company profile and chosen program on our platform.',
        details: [
            'Consultation meeting to identify wellness goals',
            'Select the program tier that fits your company',
            'Company profile and program configured on the platform',
            'Dedicated account manager assigned',
        ],
    },
    {
        step: 2,
        icon: <FaUserPlus className="text-white text-lg" />,
        iconBg: 'from-blue-500 to-cyan-500',
        title: 'Employee Onboarding',
        duration: '1 week – 1 month',
        description: 'StringsSG provides a unique registration link for your company. Employees use this link to sign up and access the wellness platform.',
        details: [
            'Unique company registration link generated',
            'Share with employees via email, Slack, or internal channels',
            'Employees self-register and set up their profiles',
            'Timeline depends on communication speed and employee count',
        ],
    },
    {
        step: 3,
        icon: <FaCalendarAlt className="text-white text-lg" />,
        iconBg: 'from-green-500 to-emerald-500',
        title: 'Session Setup & Launch',
        duration: '2–4 weeks',
        description: 'Based on the agreed program start date, StringsSG schedules fitness sessions on the platform for employees to enroll. Session frequency and types depend on the selected program.',
        details: [
            'Sessions scheduled based on agreed start date',
            'Employees enroll in sessions via the platform',
            'Number and type of sessions based on selected program',
            'First session kicks off — program is live!',
        ],
    },
]

const mediaMentions = [
    { name: 'Yahoo',           logo: 'https://logo.clearbit.com/yahoo.com',           width: 100 },
    { name: 'BestInSingapore', logo: 'https://logo.clearbit.com/bestinsingapore.co',  width: 130 },
    { name: 'Antara News',     logo: 'https://logo.clearbit.com/antaranews.com',      width: 120 },
    { name: 'Expat Living',    logo: 'https://logo.clearbit.com/expatliving.sg',      width: 120 },
    { name: 'Seedly',          logo: 'https://logo.clearbit.com/seedly.sg',           width: 100 },
]

/* ───────────────────────────── pricing plans ───────────────────────────── */
const plans = [
    {
        name: 'Starter Wellness Program',
        price: '$2,500',
        period: '/ month',
        popular: false,
        bestFor: 'Small to mid-sized companies starting their first corporate wellness initiative.',
        idealFor: [
            '30–80 employees',
            'Companies testing employee wellness programs',
            'Teams wanting weekly fitness sessions + digital support',
        ],
        features: [
            {
                icon: <FaDumbbell className="text-orange-400" />,
                title: 'Weekly Group Fitness Session',
                items: [
                    '1 onsite session per week',
                    '45–60 minute professionally guided workout',
                    'Suitable for beginners to intermediate fitness levels',
                ],
            },
            {
                icon: <FaLaptop className="text-orange-400" />,
                title: 'Employee Fitness App Access',
                items: [
                    'Personalized diet plans',
                    'Body measurement tracking',
                    'Progress photo comparisons'
                ],
            },
            // {
            //     icon: <FaTrophy className="text-orange-400" />,
            //     title: 'Monthly Corporate Fitness Challenge',
            //     items: [
            //         'Step challenges',
            //         'Workout consistency challenges',
            //         'Fat loss challenges',
            //     ],
            // },
            // {
            //     icon: <FaUsers className="text-orange-400" />,
            //     title: 'Employee Leaderboard',
            //     items: [
            //         'Friendly competition between employees to boost engagement',
            //     ],
            // },
            // {
            //     icon: <FaFileAlt className="text-orange-400" />,
            //     title: 'Quarterly Wellness Report',
            //     items: [
            //         'Employee participation',
            //         'Challenge engagement',
            //         'Fitness progress trends',
            //     ],
            // },
        ],
    },
    {
        name: 'Growth Wellness Program',
        price: '$4,500',
        period: '/ month',
        popular: true,
        bestFor: 'Companies looking for a structured wellness program with higher engagement.',
        idealFor: [
            '60–150 employees',
            'Organizations prioritizing employee health',
            'HR teams seeking measurable wellness outcomes',
        ],
        features: [
            {
                icon: <FaDumbbell className="text-orange-400" />,
                title: '2 Weekly Group Fitness Sessions',
                items: [
                    '2 onsite session per week',
                    'HIIT workouts'
                ],
            },
            {
                icon: <FaUsers className="text-orange-400" />,
                title: 'Monthly Wellness Workshop',
                items: [
                    'Interactive sessions delivered onsite or virtually'
                ],
            },
            // {
            //     icon: <FaTrophy className="text-orange-400" />,
            //     title: 'Corporate Transformation Challenges',
            //     items: [
            //         'Quarterly 8-week transformation programs',
            //         'Employees compete in fitness improvements',
            //     ],
            // },
            // {
            //     icon: <FaLaptop className="text-orange-400" />,
            //     title: 'Advanced App Features',
            //     items: [
            //         'Track workouts',
            //         'Log measurements',
            //         'Compare progress photos',
            //         'Join team challenges',
            //     ],
            // },
            {
                icon: <FaChartLine className="text-orange-400" />,
                title: 'Quarterly Wellness Analytics',
                items: [
                    'Employee engagement reports',
                    'Program participation data',
                    'Overall wellness trends',
                ],
            },
        ],
    },
    {
        name: 'Premium Corporate Wellness',
        price: '$7,000',
        period: '/ month',
        popular: false,
        bestFor: 'Large organizations wanting a comprehensive wellness program integrated into company culture.',
        idealFor: [
            '150–300 employees',
            'Companies investing in long-term employee wellbeing',
            'Organizations wanting frequent onsite training',
        ],
        features: [
            {
                icon: <FaDumbbell className="text-orange-400" />,
                title: '3 Weekly Group Fitness Sessions',
                items: [
                    '3 onsite session per week',
                    'Mobility & Stretching sessions',
                ],
            },
            // {
            //     icon: <FaTrophy className="text-orange-400" />,
            //     title: 'Company-Wide Transformation Challenges',
            //     items: [
            //         'Large-scale fitness challenges',
            //         'Increase employee participation and motivation',
            //     ],
            // },
            {
                icon: <FaChartLine className="text-orange-400" />,
                title: 'Executive Fitness Sessions',
                items: [
                    'Optional small-group training for leadership teams',
                ],
            },
            // {
            //     icon: <FaChartLine className="text-orange-400" />,
            //     title: 'Quarterly Wellness Analytics',
            //     items: [
            //         'Employee engagement reports',
            //         'Program participation data',
            //         'Overall wellness trends',
            //     ],
            // },
        ],
    },
]

/* ───────────────────────────── comparison table ───────────────────────────── */
const comparisonRows = [
    { feature: 'Weekly fitness sessions', starter: '1', growth: '2', premium: '3' },
    { feature: 'Employee wellness app', starter: '✔', growth: '✔', premium: '✔' },
    { feature: 'Corporate fitness challenges', starter: '✔', growth: '✔', premium: '✔' },
    { feature: 'Wellness workshops', starter: '—', growth: 'Monthly', premium: 'Monthly' },
    { feature: 'HR wellness reports', starter: '—', growth: 'Quarterly', premium: 'Quarterly' },
    { feature: 'Employee capacity', starter: 'Up to 80', growth: 'Up to 150', premium: 'Up to 300' },
]

/* ───────────────────────────── corporate FAQs ───────────────────────────── */
const corporateFaqs = [
    {
        question: 'How quickly can the program be set up for our company?',
        answer: 'We can typically get your corporate wellness program up and running within 2 weeks of signing. This includes trainer assignment, app setup for all employees, and the first onsite session scheduling.',
    },
    {
        question: 'Can we customize the program for our specific needs?',
        answer: 'Absolutely. All our programs are fully customizable. We work closely with HR teams to tailor session types, workshop topics, challenge formats, and reporting to align with your company\'s wellness goals.',
    },
    {
        question: 'What happens if we need to scale up the program?',
        answer: 'You can upgrade your plan at any time. We make transitions seamless — your employees keep their progress data, and we simply add more sessions, trainers, and features as needed.',
    },
    {
        question: 'Do you provide trainers onsite at our office?',
        answer: 'Yes, our certified trainers come to your office, company gym, or any preferred location. We bring all necessary equipment for group sessions and adapt workouts to available space.',
    },
    {
        question: 'How do you measure the success of the wellness program?',
        answer: 'We provide detailed HR wellness reports covering participation rates, employee engagement, fitness progress trends, and challenge results. These reports help you quantify ROI on your wellness investment.',
    },
    {
        question: 'Is there a minimum contract period?',
        answer: 'We offer flexible 3-month, 6-month, and 12-month contracts. Longer commitments come with preferential pricing. Contact us to discuss the best option for your organization.',
    },
]

/* ═══════════════════════════════ MAIN COMPONENT ═══════════════════════════════ */

export const CorporateTraining = () => {
    const [expandedFaq, setExpandedFaq] = useState(null)

    const toggleFaq = (index) => {
        setExpandedFaq(expandedFaq === index ? null : index)
    }

    return (
        <>
            {/* ─── Hero Section ─── */}
            <section className="-mt-14 pt-14 relative bg-slate-900 overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />

                <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                        <span className="text-sm">🏢</span>
                        <span className="text-xs font-medium text-white/70 uppercase tracking-wider">Corporate Wellness</span>
                    </div>
                    <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight mb-6">
                        Corporate Wellness Programs<br />
                        <span className="bg-linear-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                            Powered by Expert Trainers
                        </span>
                    </h1>
                    <p className="max-w-3xl mx-auto text-lg text-white/60 leading-relaxed mb-8">
                        Help your employees stay healthier, more productive, and engaged through a structured fitness
                        and wellness program powered by expert trainers and our digital transformation platform.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            href="/contact"
                            prefetch={false}
                            className="inline-flex justify-center items-center gap-2 px-8 py-3.5 text-white font-semibold rounded-full bg-linear-to-tr from-orange-600 to-amber-400 hover:opacity-90 transition shadow-lg shadow-orange-500/25"
                        >
                            Get a Quote <FaArrowRight className="text-sm" />
                        </Link>
                        <a
                            href="#plans"
                            className="inline-flex justify-center items-center gap-2 px-8 py-3.5 text-white/80 font-semibold rounded-full border border-orange-500 hover:bg-white/5 transition"
                        >
                            View Plans
                        </a>
                    </div>
                </div>
            </section>

            {/* ─── Trusted Partners ─── */}
            <TrustedPartners />

            {/* ─── How It Works — Onboarding Timeline ─── */}
            <section className="relative bg-slate-900 overflow-hidden">
                <div className="absolute top-0 right-1/4 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative max-w-5xl mx-auto px-4 py-16 md:py-24">
                    {/* Header */}
                    <div className="text-center mb-14">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">🚀</span>
                            <span className="text-xs font-medium text-white/70">Getting Started</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                            How It Works
                        </h2>
                        <p className="text-white/50 mt-3 text-sm max-w-2xl mx-auto leading-relaxed">
                            From the first meeting to your employees&apos; first fitness session — here&apos;s the onboarding journey.
                        </p>
                    </div>

                    {/* Timeline */}
                    <div className="relative">
                        {/* Vertical connector line — desktop */}
                        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-linear-to-b from-orange-500/40 via-blue-500/40 to-green-500/40" />

                        <div className="space-y-12 md:space-y-0">
                            {onboardingSteps.map((step, idx) => (
                                <motion.div
                                    key={step.step}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: idx * 0.15 }}
                                    className={`relative md:flex items-start gap-8 md:mb-16 last:md:mb-0 ${
                                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                                    }`}
                                >
                                    {/* ── Step card ── */}
                                    <div className={`md:w-[calc(50%-2rem)] ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300">
                                            {/* Mobile icon + step badge row */}
                                            <div className={`flex items-center gap-3 mb-4 ${idx % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                                                <div className={`w-10 h-10 rounded-xl bg-linear-to-br ${step.iconBg} flex items-center justify-center shadow-lg md:hidden`}>
                                                    {step.icon}
                                                </div>
                                                <div>
                                                    <span className="text-xs font-bold text-white/30 uppercase tracking-widest">Step {step.step}</span>
                                                    <h3 className="text-lg font-bold text-white">{step.title}</h3>
                                                </div>
                                            </div>

                                            {/* Duration badge */}
                                            <div className={`flex ${idx % 2 === 0 ? 'md:justify-end' : 'md:justify-start'} mb-3`}>
                                                <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-xs font-medium text-white/60">
                                                    <FaClock className="text-orange-400 text-[10px]" />
                                                    {step.duration}
                                                </span>
                                            </div>

                                            <p className="text-sm text-white/50 leading-relaxed mb-4">{step.description}</p>

                                            {/* Detail bullets */}
                                            <ul className="space-y-2">
                                                {step.details.map((detail, di) => (
                                                    <li key={di} className={`flex items-start gap-2 text-xs text-white/40 leading-relaxed ${idx % 2 === 0 ? 'md:flex-row-reverse md:text-right' : ''}`}>
                                                        <FaCheckCircle className={`text-orange-400/60 mt-0.5 shrink-0 ${idx % 2 === 0 ? 'md:ml-2' : ''}`} />
                                                        <span>{detail}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* ── Center node (desktop only) ── */}
                                    <div className="hidden md:flex flex-col items-center shrink-0" style={{ width: '4rem' }}>
                                        <div className={`w-14 h-14 rounded-2xl bg-linear-to-br ${step.iconBg} flex items-center justify-center shadow-lg shadow-black/30 border-2 border-slate-900 relative z-10`}>
                                            {step.icon}
                                        </div>
                                        <span className="mt-2 text-[10px] font-bold text-white/30 uppercase tracking-wider">Step {step.step}</span>
                                    </div>

                                    {/* ── Spacer for alternating layout ── */}
                                    <div className="hidden md:block md:w-[calc(50%-2rem)]" />
                                </motion.div>
                            ))}
                        </div>

                        {/* ── Total timeline summary ── */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="mt-14 md:mt-20"
                        >
                            <div className="bg-linear-to-r from-orange-500/10 to-amber-500/10 backdrop-blur-md border border-orange-400/20 rounded-2xl p-6 md:p-8 text-center max-w-2xl mx-auto">
                                <div className="flex items-center justify-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-linear-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                                        <FaClock className="text-white" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white">Total Onboarding Timeline</h3>
                                </div>
                                <p className="text-3xl md:text-4xl font-extrabold bg-linear-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent mb-2">
                                    1 – 2 Months
                                </p>
                                <p className="text-sm text-white/50 leading-relaxed">
                                    From your first consultation meeting to your employees&apos; first fitness session.
                                    Timeline varies based on company size, program selection, and employee registration speed.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ─── Media Mentions (As Seen On) ─── */}
            {/* <section className="bg-slate-900">
                <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">📰</span>
                            <span className="text-xs font-medium text-white/70">As Seen On</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                            Featured In
                        </h2>
                        <p className="text-white/50 mt-2 text-sm">
                            We&apos;ve been mentioned by leading media outlets in the region.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
                        {mediaMentions.map((media) => (
                            <div
                                key={media.name}
                                className="group flex items-center justify-center bg-white/5 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/10 hover:border-orange-400/30 hover:bg-white/10 transition-all duration-300"
                            >
                                <Image
                                    src={media.logo}
                                    alt={`${media.name} logo`}
                                    width={media.width}
                                    height={40}
                                    className="h-8 md:h-10 w-auto object-contain brightness-0 invert opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                                    unoptimized
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* ─── Pricing Plans ─── */}
            <section id="plans" className="relative bg-slate-900 overflow-hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />
                <div className="absolute top-1/2 right-0 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl" />

                <div className="relative  max-w-7xl mx-auto px-4 py-16 md:py-24">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">💰</span>
                            <span className="text-xs font-medium text-white/70">Pricing</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                            Choose Your Wellness Program
                        </h2>
                        <p className="text-white/50 mt-2 text-sm max-w-2xl mx-auto">
                            Flexible plans designed for companies of all sizes. Scale up anytime as your wellness culture grows.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
                        {plans.map((plan, idx) => (
                            <motion.div
                                key={plan.name}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: idx * 0.15 }}
                                className={`relative rounded-3xl overflow-hidden border transition-all duration-300 ${
                                    plan.popular
                                        ? 'bg-linear-to-b from-orange-500/10 to-transparent border-orange-400/40 shadow-xl shadow-orange-500/10'
                                        : 'bg-white/5 border-white/10 hover:border-white/20'
                                }`}
                            >
                                {plan.popular && (
                                    <div className="absolute top-0 left-0 right-0 bg-linear-to-r from-orange-500 to-amber-400 text-center py-2">
                                        <span className="text-sm font-bold text-white">⭐ Most Popular</span>
                                    </div>
                                )}

                                <div className={`px-6 md:px-8 py-15`}>
                                    {/* Plan Header */}
                                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                                    <div className="flex items-baseline gap-1 mb-4">
                                        <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                                        <span className="text-white/50 text-sm">{plan.period}</span>
                                    </div>

                                    {/* Best For */}
                                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                                        <p className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-1">Best for</p>
                                        <p className="text-sm text-white/60 leading-relaxed">{plan.bestFor}</p>
                                    </div>

                                    {/* Ideal For */}
                                    <div className="mb-6">
                                        <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Ideal for</p>
                                        <ul className="space-y-2">
                                            {plan.idealFor.map((item, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                                                    <FaCheckCircle className="text-orange-400 mt-0.5 shrink-0 text-xs" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-white/10 my-6" />

                                    {/* What's Included */}
                                    <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">
                                        What&apos;s Included {idx > 0 && <span className="text-orange-400">(Everything in {plans[idx - 1].name.split(' ')[0]}, plus)</span>}
                                    </p>
                                    <div className="space-y-5">
                                        {plan.features.map((feature, fi) => (
                                            <div key={fi}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    {feature.icon}
                                                    <h4 className="text-sm font-semibold text-white/90">{feature.title}</h4>
                                                </div>
                                                <ul className="space-y-1.5 pl-6">
                                                    {feature.items.map((item, ii) => (
                                                        <li key={ii} className="text-xs text-white/50 leading-relaxed flex items-start gap-2">
                                                            <span className="text-orange-400/60 mt-0.5">•</span>
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>

                                    {/* CTA */}
                                    <div className="mt-8">
                                        <Link
                                            href="/contact"
                                            prefetch={false}
                                            className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-full transition text-sm ${
                                                plan.popular
                                                    ? 'bg-linear-to-tr from-orange-600 to-amber-400 text-white shadow-lg shadow-orange-500/25 hover:opacity-90'
                                                    : 'border border-white/20 text-white/80 hover:bg-white/5'
                                            }`}
                                        >
                                            Get Started <FaArrowRight className="text-xs" />
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Comparison Table ─── */}
            <section className="relative bg-slate-900 overflow-hidden">
                <div className="absolute top-0 right-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                <div className="relative  max-w-5xl mx-auto px-3 py-16 md:py-20">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">📊</span>
                            <span className="text-xs font-medium text-white/70">Compare</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                            Compare Programs
                        </h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="text-left py-4 px-4 text-sm font-semibold text-white/60">Feature</th>
                                    <th className="text-center py-4 px-4 text-sm font-semibold text-white/80">Starter</th>
                                    <th className="text-center py-4 px-4 text-sm font-semibold text-orange-400">
                                        Growth
                                        <span className="block text-xs text-orange-400/60">Most Popular</span>
                                    </th>
                                    <th className="text-center py-4 px-4 text-sm font-semibold text-white/80">Premium</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonRows.map((row, i) => (
                                    <tr key={i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/2' : ''}`}>
                                        <td className="py-4 px-4 text-sm text-white/60">{row.feature}</td>
                                        <td className="py-4 px-4 text-sm text-white/70 text-center font-medium">{row.starter}</td>
                                        <td className="py-4 px-4 text-sm text-orange-300 text-center font-medium">{row.growth}</td>
                                        <td className="py-4 px-4 text-sm text-white/70 text-center font-medium">{row.premium}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ─── Why Companies Choose Us ─── */}
            <section className="relative bg-slate-900 overflow-hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />

                <div className="relative  max-w-6xl mx-auto px-4 py-16 md:py-24">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">🏆</span>
                            <span className="text-xs font-medium text-white/70">Why Us</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                            Why Companies Choose Our Program
                        </h2>
                        <p className="max-w-3xl mx-auto text-white/50 mt-3 leading-relaxed">
                            Our program combines expert trainers and a digital transformation platform to create
                            measurable improvements in employee wellbeing.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Employee Benefits */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="bg-white/5 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/10"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-linear-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                                    <FaUsers className="text-white text-lg" />
                                </div>
                                <h3 className="text-lg font-bold text-white">Employees Benefit From</h3>
                            </div>
                            <ul className="space-y-3">
                                {[
                                    'Structured fitness programs',
                                    'Personalized diet plans',
                                    'Progress tracking',
                                    'Team challenges',
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-white/60">
                                        <FaCheckCircle className="text-green-400 shrink-0" />
                                        <span className="text-sm">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* HR Benefits */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="bg-white/5 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/10"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-linear-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                                    <FaChartLine className="text-white text-lg" />
                                </div>
                                <h3 className="text-lg font-bold text-white">HR Teams Gain</h3>
                            </div>
                            <ul className="space-y-3">
                                {[
                                    'Measurable wellness insights',
                                    'Improved employee engagement',
                                    'Healthier workplace culture',
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-white/60">
                                        <FaCheckCircle className="text-orange-400 shrink-0" />
                                        <span className="text-sm">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>

                    {/* CTA Banner */}
                    <div className="mt-16 bg-linear-to-r from-orange-500/10 to-amber-500/10 backdrop-blur-md border border-orange-400/20 rounded-3xl p-8 md:p-12 text-center">
                        <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
                            Ready to Transform Your Workplace?
                        </h3>
                        <p className="text-white/50 mb-6 max-w-2xl mx-auto">
                            Join Google, Amazon, JPMorgan Chase, and other leading companies that trust StringsSG
                            for their corporate wellness programs.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                href="/contact"
                                prefetch={false}
                                className="inline-flex justify-center items-center gap-2 px-8 py-3.5 text-white font-semibold rounded-full bg-linear-to-tr from-orange-600 to-amber-400 hover:opacity-90 transition shadow-lg shadow-orange-500/25"
                            >
                                Contact Us Today <FaArrowRight className="text-sm" />
                            </Link>
                            <Link
                                href="/sg/personal-trainer"
                                prefetch={false}
                                className="inline-flex justify-center items-center gap-2 px-8 py-3.5 text-white/80 font-semibold rounded-full border border-orange-500 hover:bg-white/5 transition"
                            >
                                Browse Our Trainers
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── Quarterly Wellness Charts ─── */}
            <CorporateWellnessCharts />

            {/* ─── FAQ ─── */}
            <section className="relative bg-slate-900 overflow-hidden">
                <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                <div className="relative  max-w-4xl mx-auto px-4 py-16 md:py-20">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">❓</span>
                            <span className="text-xs font-medium text-white/70">FAQs</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-sm text-white/50 mt-2">
                            Everything you need to know about our corporate wellness programs
                        </p>
                    </div>

                    <div className="space-y-3">
                        {corporateFaqs.map((item, index) => (
                            <div
                                key={index}
                                className={`rounded-2xl overflow-hidden transition-all duration-300 border ${
                                    expandedFaq === index
                                        ? 'bg-white/10 border-orange-400/30'
                                        : 'bg-white/5 border-white/10 hover:bg-white/[0.07] hover:border-white/20'
                                }`}
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full px-5 py-4 text-left flex justify-between items-center gap-3"
                                >
                                    <span className="text-sm font-semibold text-white/90">{item.question}</span>
                                    <motion.span
                                        animate={{ rotate: expandedFaq === index ? 180 : 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="text-orange-400 shrink-0"
                                    >
                                        <FaChevronDown className="text-xs" />
                                    </motion.span>
                                </button>

                                <motion.div
                                    initial={false}
                                    animate={{
                                        height: expandedFaq === index ? 'auto' : 0,
                                        opacity: expandedFaq === index ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="text-sm px-5 pb-4 text-white/50 leading-relaxed">
                                        {item.answer}
                                    </div>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    )
}
